import axios from "axios";
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { API_URL, TOKEN } from "../../../variables/constants";

const AddAccessoires = ({
  show,
  handleClose,
  editMode,
  selectedAccesoire, // Typo corrected to selectedAccessoire in parent component
  onAddSuccess,
  onUpdateSuccess, // New prop for update success
  existingAccessoires, // New prop to receive existing data for client-side check
}) => {
  const [loading, setLoading] = useState(false);
  const [montant, setMontant] = useState(""); // Renamed 'nom' to 'montant' for clarity
  const [selectedCategorie, setSelectedCategorie] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // Fetch categories regardless of edit mode or modal visibility
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/categorie`, {
          headers: {
            Authorization: `Bearer ${TOKEN}`,
          },
        });
        setCategories(res.data);
      } catch (err) {
        console.error(err);
        toast.error("Erreur lors de la récupération des catégories.");
      }
    };

    fetchCategories();

    // Set form fields based on edit mode and selected accessory
    if (editMode && selectedAccesoire) {
      // libelleParametre stores the amount
      setMontant(selectedAccesoire.libelleParametre || "");
      setSelectedCategorie(selectedAccesoire.categorie?.categorieId || "");
    } else {
      setMontant("");
      setSelectedCategorie("");
    }
  }, [show, editMode, selectedAccesoire]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const trimmedMontant = String(montant).trim(); // Ensure it's a string for trimming
    const selectedCat = categories.find(
      (cat) => cat.categorieId === parseInt(selectedCategorie, 10)
    );

    // --- Duplicate Check Logic ---
    if (!editMode) {
      // For adding, check against all existing accessoires
      const isDuplicate = existingAccessoires.some(
        (item) =>
          String(item.libelleParametre).trim() === trimmedMontant &&
          item.categorie?.categorieId === selectedCat?.categorieId
      );

      if (isDuplicate) {
        toast.warn("Un accessoire avec ce montant et cette catégorie existe déjà.");
        setLoading(false);
        return;
      }
    } else {
      // For editing, check against existing accessoires EXCLUDING the one being edited
      const isDuplicate = existingAccessoires.some(
        (item) =>
          item.parametreId !== selectedAccesoire.parametreId && // Exclude the current item
          String(item.libelleParametre).trim() === trimmedMontant &&
          item.categorie?.categorieId === selectedCat?.categorieId
      );

      if (isDuplicate) {
        toast.warn("Un autre accessoire avec ce montant et cette catégorie existe déjà.");
        setLoading(false);
        return;
      }
    }
    // --- End Duplicate Check Logic ---

    const accessoireData = {
      ...selectedAccesoire, // Keep existing ID for PUT request
      libelleParametre: trimmedMontant, // The amount is stored here
      typeParametre: "ACCESSOIRES",
      categorie: selectedCat || null, // Ensure category is null if not found
    };

    try {
      const response = editMode
        ? await axios.put(`${API_URL}/api/parametre`, accessoireData, {
            headers: {
              Authorization: `Bearer ${TOKEN}`,
              "Content-Type": "application/json",
            },
          })
        : await axios.post(`${API_URL}/api/parametre`, accessoireData, {
            headers: {
              Authorization: `Bearer ${TOKEN}`,
              "Content-Type": "application/json",
            },
          });

      toast.success(editMode ? "Accessoire modifié avec succès" : "Accessoire ajouté avec succès");

      if (!editMode) {
        onAddSuccess(response.data); // For adding to the top of the list
      } else {
        onUpdateSuccess(response.data); // Call new update success handler
      }
      handleClose(); // Close modal after success
    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 409) { // Assuming 409 for conflict/duplicate from API
        toast.error("Erreur : Cet accessoire existe déjà sur le serveur.");
      } else {
        toast.error("Erreur lors de l'enregistrement de l'accessoire.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Body>
        <button
          type="button"
          className="btn-close btn-primary"
          onClick={handleClose}
          aria-label="Close"
        ></button>
        <div className="text-center mb-4">
          <h4 className="address-title mb-2">
            {editMode ? "Modifier l'accessoire" : "Ajouter un accessoire"}
          </h4>
          <p className="address-subtitle">
            {editMode ? "Modifier l'accessoire existant" : "Ajouter un nouvel accessoire"}
          </p>
        </div>
        <form className="row g-3" onSubmit={onSubmit}>
          <div className="col-12">
            <label className="form-label" htmlFor="accessoireMontant">Montant</label>
            <input
              type="number" // Use type="number" for amounts
              id="accessoireMontant"
              onChange={(e) => setMontant(e.target.value)}
              className="form-control"
              placeholder="Montant de l'accessoire"
              name="montant"
              value={montant}
              required
            />
          </div>
          <div className="col-12">
            <label className="form-label" htmlFor="accessoireCategory">Catégorie</label>
            <select
              id="accessoireCategory"
              name="accessoireCategory"
              className="select2 form-select"
              onChange={(e) => setSelectedCategorie(e.target.value)}
              value={selectedCategorie}
              required
            >
              <option value="">Sélectionner la catégorie</option>
              {categories.map((cat) => (
                <option key={cat.categorieId} value={cat.categorieId}>
                  {cat.nom}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 text-center">
            <button
              type="submit"
              className="btn btn-primary me-3"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-grow spinner-grow-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </button>
            <button
              type="button"
              className="btn btn-label-secondary"
              onClick={handleClose}
            >
              Annuler
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AddAccessoires;